import axios from 'axios';
import { API_BASE_URL, AUTH_PREFIX_PATH, UNAUTHENTICATED_ENTRY } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import AgentService from 'services/AgentService';
import store from '../store';
import { IS_IMPERSONATING } from 'constants/AuthConstant';
import tokenStorage from 'utils/tokenStorage';
import { notification } from 'antd';

// Only 401 and 403 should trigger logout, not 400 (validation errors)
const unauthorizedCode = [401, 403]
const sessionTokenKeys = ['impersonation_token', 'auth_token', 'admin_token']
let sessionExpiredHandled = false

function decodeJwtPayload(token) {
	try {
		const base64Url = token.split('.')[1]
		if (!base64Url) return null
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
				.join('')
		)
		return JSON.parse(jsonPayload)
	} catch (_) {
		return null
	}
}

function isJwtExpired(token) {
	const payload = decodeJwtPayload(token)
	if (!payload?.exp) return false
	const nowInSeconds = Math.floor(Date.now() / 1000)
	return payload.exp <= nowInSeconds
}

function clearSessionAuthData() {
	[
		'user_role',
		'user_id',
		'user_email',
		'user_first_name',
		'user_last_name',
		'user_permissions',
		'is_impersonating',
		'impersonated_manager_id',
		'impersonated_agent_id',
		'impersonated_staff_id',
		'impersonated_user_id',
		'impersonated_role',
		'impersonation_token',
		'original_role',
		'auth_token',
		'admin_token'
	].forEach((key) => tokenStorage.removeItem(key))
}

async function handleSessionExpired(message = 'Session expired') {
	if (sessionExpiredHandled) return
	if (typeof window !== 'undefined' && window.location.hash.includes('/public/reports/')) {
		return
	}
	sessionExpiredHandled = true
	if (tokenStorage.getItem('user_role') === 'agent') {
		try {
			await AgentService.updateAgentStatus('offline');
		} catch (_) {
			// session may already be invalid
		}
	}
	clearSessionAuthData()
	store.dispatch(signOutSuccess())
	notification.error({
		message,
		description: 'Please login again'
	})
	if (typeof window !== 'undefined' && !window.location.pathname.startsWith(AUTH_PREFIX_PATH)) {
		window.location.assign(`${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}`)
	}
}

function stableParamsSerialize(params) {
	if (params == null || typeof params !== 'object') return ''
	const keys = Object.keys(params).sort()
	const sorted = {}
	keys.forEach((k) => {
		sorted[k] = params[k]
	})
	return JSON.stringify(sorted)
}

function getGetDedupeKey(config) {
	const method = (config.method || 'get').toLowerCase()
	if (method !== 'get') return null
	const baseURL = config.baseURL ?? ''
	const url = config.url || ''
	const paramsStr = stableParamsSerialize(config.params)
	const isImpersonating = tokenStorage.getItem(IS_IMPERSONATING) === 'true'
	let imp = ''
	if (isImpersonating) {
		imp = `${tokenStorage.getItem('impersonated_manager_id') || ''}|${tokenStorage.getItem('impersonated_agent_id') || ''}`
	}
	return `GET|${baseURL}|${url}|${paramsStr}|${imp}`
}

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  // Required so the browser sends the httpOnly auth cookie with every request
  withCredentials: true,
})

// API Request interceptor
service.interceptors.request.use(config => {
	const requestUrl = config?.url || ''
	const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/')
	const isPublicReportRequest = requestUrl.includes('/public/reports/')
	const onPublicReportPage = typeof window !== 'undefined' && window.location.hash.includes('/public/reports/')
	if (!isAuthRequest && !isPublicReportRequest && !onPublicReportPage) {
		const expiredSessionToken = sessionTokenKeys.find((key) => {
			const token = tokenStorage.getItem(key)
			return !!token && isJwtExpired(token)
		})
		if (expiredSessionToken) {
			handleSessionExpired('Session expired')
			return Promise.reject(new Error('Session expired'))
		}
	}

	// The JWT is stored in an httpOnly cookie — the browser sends it automatically.
	// No need to read or set the Authorization header for normal browser requests.

	// Impersonation headers (IDs only, not tokens)
	const isImpersonating = tokenStorage.getItem(IS_IMPERSONATING) === 'true';
	if (isImpersonating) {
		const impersonatedManagerId = tokenStorage.getItem('impersonated_manager_id');
		if (impersonatedManagerId) {
			config.headers['X-Impersonated-Manager-Id'] = impersonatedManagerId;
		}
		const impersonatedAgentId = tokenStorage.getItem('impersonated_agent_id');
		if (impersonatedAgentId) {
			config.headers['X-Impersonated-Agent-Id'] = impersonatedAgentId;
		}
	}

	// Don't set Content-Type for FormData, let browser set it automatically with boundary
	if (config.data instanceof FormData) {
		delete config.headers['Content-Type'];
	}

  	return config
}, error => {
	notification.error({
		message: 'Error'
	})
	Promise.reject(error)
})

// API response interceptor
service.interceptors.response.use( (response) => {
	// Check if response data indicates logout is required
	if (response.data && response.data.logout === true) {
		handleSessionExpired(response.data.msg || 'Session expired')
		return Promise.reject(new Error('Session expired'));
	}
	return response.data
}, (error) => {
	if (axios.isCancel(error)) {
		return Promise.reject(error)
	}
	if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
		return Promise.reject(error)
	}

	// Check if error.response exists (network errors don't have response)
	if (!error.response) {
		notification.error({
			message: 'Network Error',
			description: 'Cannot connect to server. Please check if the backend is running.'
		})
		return Promise.reject(error);
	}

	const requestUrl = error.config?.url || '';
	const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/');
	const isPublicReportRequest = requestUrl.includes('/public/reports/');

	// For auth requests (login, etc.), don't show notification - let the component handle it
	if (isAuthRequest) {
		return Promise.reject(error);
	}

	if (isPublicReportRequest) {
		return Promise.reject(error);
	}

	// Only logout on 401 (Unauthorized) or 403 (Forbidden), not on 400 (Bad Request)
	if (unauthorizedCode.includes(error.response.status)) {
		handleSessionExpired('Session expired')
		return Promise.reject(error);
	}

	// Handle 400 Bad Request (validation errors)
	if (error.response.status === 400) {
		const errorMessage = error.response.data?.message ||
		                     error.response.data?.msg ||
		                     error.response.data?.errors?.[0] ||
		                     'Invalid request. Please check your input.';
		notification.error({
			message: 'Validation Error',
			description: errorMessage
		})
		return Promise.reject(error);
	}

	// Handle plan expired/required error (409 CONFLICT)
	if (error.response.status === 409) {
		const errorData = error.response.data;
		const errorCode = errorData?.code || errorData?.errorCode;
		const errorMessage = errorData?.message || errorData?.msg || 'An error occurred';

		if (
			errorCode === 'CLIENT_EMAIL_EXISTS' ||
			errorCode === 'USER_EMAIL_EXISTS' ||
			errorCode === 'AGENT_EMAIL_EXISTS' ||
			errorCode === 'STAFF_EMAIL_EXISTS' ||
			errorCode === 'MANAGER_EMAIL_EXISTS' ||
			errorCode === 'PLAN_AGENT_ACCESS_DENIED' ||
			errorCode === 'PLAN_DIALER_DISABLED' ||
			errorCode === 'PLAN_AGENT_IA_DISABLED' ||
			errorCode === 'PLAN_CALL_BROADCAST_DISABLED'
		) {
			return Promise.reject(error);
		}
		
		if (errorCode === 'PLAN_REQUIRED' || errorCode === 'PLAN_EXPIRED') {
			notification.error({
				message: 'Plan Subscription Required',
				description: errorMessage,
				duration: 8
			});
			return Promise.reject(error);
		}
		
		notification.error({
			message: 'Conflict',
			description: errorMessage
		});
		return Promise.reject(error);
	}

	// Handle rate limit error (429)
	if (error.response.status === 429) {
		const message = error.response.data?.message || 'Trop de tentatives. Veuillez réessayer plus tard.';
		notification.warning({
			message: 'Rate Limit',
			description: message
		})
		return Promise.reject(error);
	}

	if (error.response.status === 404) {
		notification.error({ message: 'Not Found' })
	}

	if (error.response.status === 500) {
		notification.error({ message: 'Internal Server Error' })
	}

	if (error.response.status === 508) {
		notification.error({ message: 'Time Out' })
	}

	return Promise.reject(error);
});

// Une seule requête HTTP par GET identique tant que la précédente n'est pas terminée (évite doublons résiduels).
const pendingGetRequests = new Map()
const rawRequest = service.request.bind(service)
service.request = function requestWithGetDedupe(config) {
	const mergedBase = config.baseURL ?? service.defaults.baseURL ?? API_BASE_URL ?? ''
	const key = getGetDedupeKey({ ...config, baseURL: mergedBase })
	if (!key) {
		return rawRequest(config)
	}
	if (pendingGetRequests.has(key)) {
		return pendingGetRequests.get(key)
	}
	const promise = rawRequest(config).finally(() => {
		pendingGetRequests.delete(key)
	})
	pendingGetRequests.set(key, promise)
	return promise
}

export default service
